import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Plus, Trash2, Edit2, Save, RefreshCw, Link2, AlertTriangle,
  ChevronDown, ChevronUp, Settings, Eye
} from 'lucide-react';

const CAMPOS_TEMPLATE = [
  { key: 'descripcion_operatoria', label: 'Descripción Operatoria', placeholder: 'Describa las operaciones detectadas...' },
  { key: 'analisis_so', label: 'Análisis del Sujeto Obligado', placeholder: 'Análisis realizado por el SO...' },
  { key: 'documentacion_respaldo', label: 'Documentación de Respaldo', placeholder: 'Documentación que respalda el reporte...' },
  { key: 'conclusiones', label: 'Conclusiones', placeholder: 'Conclusiones del análisis...' },
];

const RosConfig = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ nombre: '', descripcion_operatoria: '', analisis_so: '', documentacion_respaldo: '', conclusiones: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const [inusualidades, setInusualidades] = useState([]);
  const [monitores, setMonitores] = useState([]);
  const [loadingInus, setLoadingInus] = useState(false);
  const [expandedInus, setExpandedInus] = useState(null);
  const [mapeoOpen, setMapeoOpen] = useState(null);
  const [selectedMonitors, setSelectedMonitors] = useState([]);
  const [savingMapeo, setSavingMapeo] = useState(false);
  const [filterInus, setFilterInus] = useState('');

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try { setTemplates((await api.get('/ros-config/templates')).data); }
    catch { toast.error('Error cargando templates'); }
    finally { setLoadingTemplates(false); }
  }, []);

  const loadInusualidades = useCallback(async () => {
    setLoadingInus(true);
    try {
      const res = await api.get('/ros-config/inusualidad-tipos/mapeo-completo');
      setInusualidades(res.data.tipos || []);
      setMonitores(res.data.monitores_disponibles || []);
    } catch {
      try {
        await api.post('/ros-config/inusualidad-tipos/seed');
        const res = await api.get('/ros-config/inusualidad-tipos/mapeo-completo');
        setInusualidades(res.data.tipos || []);
        setMonitores(res.data.monitores_disponibles || []);
      } catch { toast.error('Error cargando inusualidades'); }
    }
    finally { setLoadingInus(false); }
  }, []);

  useEffect(() => { loadTemplates(); loadInusualidades(); }, [loadTemplates, loadInusualidades]);

  // ====== Templates ======
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ nombre: '', descripcion_operatoria: '', analisis_so: '', documentacion_respaldo: '', conclusiones: '' });
    setIsTemplateOpen(true);
  };
  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({ nombre: t.nombre, descripcion_operatoria: t.descripcion_operatoria || '', analisis_so: t.analisis_so || '', documentacion_respaldo: t.documentacion_respaldo || '', conclusiones: t.conclusiones || '' });
    setIsTemplateOpen(true);
  };
  const handleSaveTemplate = async () => {
    if (!templateForm.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await api.put(`/ros-config/templates/${editingTemplate.id}`, templateForm);
        toast.success('Template actualizado');
      } else {
        await api.post('/ros-config/templates', templateForm);
        toast.success('Template creado');
      }
      setIsTemplateOpen(false);
      loadTemplates();
    } catch { toast.error('Error guardando'); }
    finally { setSavingTemplate(false); }
  };
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('¿Eliminar este template?')) return;
    try { await api.delete(`/ros-config/templates/${id}`); toast.success('Eliminado'); loadTemplates(); }
    catch { toast.error('Error'); }
  };

  // ====== Inusualidad ======
  const openMapeo = (inus) => { setMapeoOpen(inus.id); setSelectedMonitors(inus.monitor_ids || []); };
  const toggleMonitor = (mid) => setSelectedMonitors(p => p.includes(mid) ? p.filter(m => m !== mid) : [...p, mid]);
  const handleSaveMapeo = async (inusId) => {
    setSavingMapeo(true);
    try {
      await api.put(`/ros-config/inusualidad-tipos/${inusId}/mapeo`, { inusualidad_id: inusId, monitor_ids: selectedMonitors });
      toast.success('Mapeo guardado'); setMapeoOpen(null); loadInusualidades();
    } catch { toast.error('Error'); }
    finally { setSavingMapeo(false); }
  };

  const filledCount = (t) => CAMPOS_TEMPLATE.filter(c => t[c.key]?.trim()).length;
  const filteredInus = filterInus ? inusualidades.filter(i => i.texto_corto.toLowerCase().includes(filterInus.toLowerCase()) || i.codigo.toLowerCase().includes(filterInus.toLowerCase())) : inusualidades;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings size={22} /> Configuración ROS
        </h1>
        <p className="text-slate-500 text-sm mt-1">Templates de texto y tipos de inusualidad para reportes a la UIF</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1">
        {[
          { id: 'templates', label: 'Templates', icon: FileText, count: templates.length },
          { id: 'inusualidad', label: 'Tipos de Inusualidad', icon: AlertTriangle, count: inusualidades.length },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${activeTab === tab.id ? 'bg-slate-900 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={15} />
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20' : 'bg-slate-300/50'}`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* ====== TEMPLATES ====== */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Cada template incluye los 4 campos de texto del ROS</p>
            <Button onClick={openNewTemplate} size="sm" data-testid="new-template-btn">
              <Plus size={14} className="mr-1" /> Nuevo Template
            </Button>
          </div>

          {loadingTemplates ? (
            <div className="text-center py-12"><RefreshCw size={24} className="animate-spin mx-auto text-slate-300" /></div>
          ) : templates.length === 0 ? (
            <Card className="p-12 text-center border-dashed border-2">
              <FileText size={36} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-600">Sin templates</p>
              <p className="text-sm text-slate-400 mt-1">Creá un template con los textos para los reportes ROS</p>
              <Button onClick={openNewTemplate} variant="outline" size="sm" className="mt-4"><Plus size={14} className="mr-1" /> Crear primer template</Button>
            </Card>
          ) : (
            <div className="grid gap-3">
              {templates.map(t => (
                <Card key={t.id} className="p-0 overflow-hidden hover:shadow-md transition-shadow" data-testid={`template-${t.id}`}>
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <FileText size={18} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{t.nombre}</p>
                        <p className="text-xs text-slate-400">
                          {filledCount(t)}/4 campos completados
                          {t.created_by && <span> &middot; {t.created_by}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="flex gap-0.5 mr-3">
                        {CAMPOS_TEMPLATE.map(c => (
                          <div key={c.key} className={`w-2 h-2 rounded-full ${t[c.key]?.trim() ? 'bg-green-400' : 'bg-slate-200'}`} title={`${c.label}: ${t[c.key]?.trim() ? 'Completado' : 'Vacío'}`} />
                        ))}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setPreviewTemplate(previewTemplate === t.id ? null : t.id)} title="Vista previa">
                        <Eye size={15} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditTemplate(t)} title="Editar">
                        <Edit2 size={15} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteTemplate(t.id)} title="Eliminar">
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </div>
                  {previewTemplate === t.id && (
                    <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
                      {CAMPOS_TEMPLATE.map(c => (
                        <div key={c.key}>
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{c.label}</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-2 min-h-[40px]">
                            {t[c.key]?.trim() || <span className="text-slate-300 italic">Sin contenido</span>}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ====== INUSUALIDAD ====== */}
      {activeTab === 'inusualidad' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Input placeholder="Buscar tipo de inusualidad..." value={filterInus} onChange={e => setFilterInus(e.target.value)} className="max-w-sm" data-testid="filter-inus" />
            <p className="text-sm text-slate-400">{filteredInus.length} tipos</p>
          </div>

          {loadingInus ? (
            <div className="text-center py-12"><RefreshCw size={24} className="animate-spin mx-auto text-slate-300" /></div>
          ) : (
            <div className="space-y-1">
              {filteredInus.map(inus => {
                const mapped = inus.monitor_ids?.length || 0;
                return (
                  <div key={inus.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`inus-${inus.codigo}`}>
                    <div className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedInus(expandedInus === inus.id ? null : inus.id)}
                    >
                      <Badge variant="outline" className="font-mono text-xs shrink-0 w-16 justify-center">{inus.codigo}</Badge>
                      <span className="text-sm text-slate-800 flex-1">{inus.texto_corto}</span>
                      {mapped > 0 && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                          <Link2 size={10} className="mr-1" />{mapped}
                        </Badge>
                      )}
                      {expandedInus === inus.id ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                    </div>

                    {expandedInus === inus.id && (
                      <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50/50">
                        <p className="text-xs text-slate-600 mt-3 mb-3 leading-relaxed">{inus.texto_completo}</p>

                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-slate-500">Monitores vinculados</span>
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => mapeoOpen === inus.id ? setMapeoOpen(null) : openMapeo(inus)} data-testid={`mapeo-btn-${inus.codigo}`}>
                            <Link2 size={11} className="mr-1" /> {mapeoOpen === inus.id ? 'Cerrar' : 'Editar mapeo'}
                          </Button>
                        </div>

                        {mapped === 0 && mapeoOpen !== inus.id && <p className="text-xs text-slate-400 italic">Sin monitores vinculados</p>}
                        {mapped > 0 && mapeoOpen !== inus.id && (
                          <div className="flex flex-wrap gap-1">
                            {inus.monitores_detalle?.map(m => (
                              <Badge key={m.id} variant="outline" className="text-xs bg-white">{m.nombre || m.id}</Badge>
                            ))}
                          </div>
                        )}

                        {mapeoOpen === inus.id && (
                          <div className="mt-2 p-3 bg-white rounded-lg border border-blue-200">
                            <div className="max-h-48 overflow-y-auto space-y-0.5">
                              {monitores.length === 0 ? (
                                <p className="text-xs text-slate-400">No hay monitores en el sistema</p>
                              ) : monitores.map(m => (
                                <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-blue-50 px-2 py-1.5 rounded">
                                  <input type="checkbox" checked={selectedMonitors.includes(m.id)} onChange={() => toggleMonitor(m.id)} className="w-3.5 h-3.5 rounded" />
                                  <span className="text-slate-700 text-xs">{m.nombre}</span>
                                </label>
                              ))}
                            </div>
                            <div className="flex gap-2 mt-2 pt-2 border-t border-slate-100">
                              <Button size="sm" className="h-7 text-xs" onClick={() => handleSaveMapeo(inus.id)} disabled={savingMapeo} data-testid="save-mapeo-btn">
                                {savingMapeo ? <RefreshCw size={11} className="animate-spin mr-1" /> : <Save size={11} className="mr-1" />}
                                Guardar
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setMapeoOpen(null)}>Cancelar</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ====== MODAL TEMPLATE ====== */}
      <Dialog open={isTemplateOpen} onOpenChange={setIsTemplateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Editar Template' : 'Nuevo Template ROS'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label className="font-semibold">Nombre del Template</Label>
              <Input value={templateForm.nombre} onChange={e => setTemplateForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Template estándar PSP" data-testid="template-nombre" className="mt-1" />
            </div>
            <p className="text-xs text-slate-400 -mt-2">
              Variables: {'{nombre}'}, {'{cuit}'}, {'{monto}'}, {'{fecha_inicio}'}, {'{fecha_fin}'}, {'{cvu}'}, {'{alertas}'}, {'{movimientos}'}
            </p>
            {CAMPOS_TEMPLATE.map(campo => (
              <div key={campo.key}>
                <Label className="font-semibold text-slate-700">{campo.label}</Label>
                <Textarea
                  value={templateForm[campo.key]}
                  onChange={e => setTemplateForm(f => ({ ...f, [campo.key]: e.target.value }))}
                  placeholder={campo.placeholder}
                  className="mt-1 min-h-[100px]"
                  data-testid={`template-${campo.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTemplateOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate} data-testid="save-template-btn">
              {savingTemplate ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              {editingTemplate ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RosConfig;
